import { ReactNode } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { AppBar, Box, Stack, Toolbar, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project import
import IconButton from 'components/@extended/IconButton';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import { DRAWER_WIDTH } from 'config';
import { ThemeMode } from 'types/config';

// assets
import { LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';

// ==============================|| MAIN LAYOUT - HEADER (same chrome as front-end) ||============================== //

interface Props {
  open: boolean;
  onToggle: () => void;
  title: ReactNode;
}

const AdminHeader = ({ open, onToggle, title }: Props) => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const { logout } = useAuth();
  const { mode, onChangeMode } = useConfig();
  const isDark = mode === ThemeMode.DARK;

  const iconBackColorOpen = isDark ? 'grey.200' : 'grey.300';
  const iconBackColor = isDark ? 'background.default' : 'grey.100';

  const squareBtn = {
    color: 'text.primary',
    bgcolor: iconBackColor,
    borderRadius: '12px',
    border: '1px solid var(--nv-border-strong)',
    width: 38,
    height: 38
  };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        zIndex: 1200,
        width: 'auto',
        left: downLG ? 0 : open ? DRAWER_WIDTH : theme.spacing(7.5),
        right: 0,
        transition: theme.transitions.create('left', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.shorter }),
        borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.78),
        backdropFilter: 'blur(18px)',
        boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, isDark ? 0.32 : 0.06)}`
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, lg: 60 },
          px: { xs: 1.25, md: 2, lg: 2.25 },
          gap: 0.75,
          '&.MuiToolbar-root': { minHeight: { xs: 56, lg: 60 } }
        }}
      >
        <IconButton
          aria-label="open drawer"
          onClick={onToggle}
          edge="start"
          color="secondary"
          variant="light"
          sx={{
            color: 'text.primary',
            bgcolor: open ? iconBackColorOpen : iconBackColor,
            border: '1px solid',
            borderColor: alpha(theme.palette.grey[500], 0.12),
            borderRadius: '12px',
            ml: { xs: 0, lg: -0.25 },
            mr: { xs: 0.25, lg: 0.75 },
            width: 38,
            height: 38,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
          }}
        >
          {!open ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}>
          <Typography variant="h5" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
              <IconButton
                aria-label="toggle color mode"
                color="secondary"
                variant="light"
                sx={squareBtn}
                onClick={() => onChangeMode(isDark ? ThemeMode.LIGHT : ThemeMode.DARK)}
              >
                {isDark ? <SunOutlined /> : <MoonOutlined />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Log out">
              <IconButton aria-label="Log out" color="secondary" variant="light" sx={squareBtn} onClick={logout}>
                <LogoutOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AdminHeader;
